import { useState, useEffect, useCallback } from 'react';
import { CPEFilters, PlantType, CPEStatus, CommercialStatus, InternalPositioning } from '../types/cpe';

const STORAGE_KEY = 'om-map-filters';
const ZONES_STORAGE_KEY = 'om-map-visible-zones';
const THEME_STORAGE_KEY = 'om-map-theme';
const ZONES_INITIALIZED_KEY = 'om-zones-initialized';

// Helper to safely parse stored filters
const parseStoredFilters = (stored: string | null): CPEFilters => {
  if (!stored) return {};

  try {
    const parsed = JSON.parse(stored);
    // Validate and clean the filters
    const cleaned: CPEFilters = {};

    if (parsed.search && typeof parsed.search === 'string') {
      cleaned.search = parsed.search;
    }
    if (parsed.plant_type && Array.isArray(parsed.plant_type)) {
      cleaned.plant_type = parsed.plant_type as PlantType[];
    }
    if (parsed.internal_positioning && Array.isArray(parsed.internal_positioning)) {
      cleaned.internal_positioning = parsed.internal_positioning as InternalPositioning[];
    }
    if (parsed.cpe_status && Array.isArray(parsed.cpe_status)) {
      cleaned.cpe_status = parsed.cpe_status as CPEStatus[];
    }
    if (parsed.commercial_status && Array.isArray(parsed.commercial_status)) {
      cleaned.commercial_status = parsed.commercial_status as CommercialStatus[];
    }
    if (parsed.om_account_manager && Array.isArray(parsed.om_account_manager)) {
      cleaned.om_account_manager = parsed.om_account_manager;
    }
    if (parsed.zone_id && Array.isArray(parsed.zone_id)) {
      cleaned.zone_id = parsed.zone_id;
    }
    if (parsed.energization_year !== undefined) {
      cleaned.energization_year = Number(parsed.energization_year);
    }
    if (parsed.min_power !== undefined) {
      cleaned.min_power = Number(parsed.min_power);
    }
    if (parsed.max_power !== undefined) {
      cleaned.max_power = Number(parsed.max_power);
    }
    if (parsed.updated_from && typeof parsed.updated_from === 'string') {
      cleaned.updated_from = parsed.updated_from;
    }
    if (parsed.updated_to && typeof parsed.updated_to === 'string') {
      cleaned.updated_to = parsed.updated_to;
    }
    if (parsed.is_waldevar_pp !== undefined && typeof parsed.is_waldevar_pp === 'boolean') {
      cleaned.is_waldevar_pp = parsed.is_waldevar_pp;
    }

    return cleaned;
  } catch (error) {
    console.error('Error parsing stored filters:', error);
    return {};
  }
};

export const useMapFilters = () => {
  // Initialize from localStorage
  const [filters, setFilters] = useState<CPEFilters>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return parseStoredFilters(stored);
  });

  const [visibleZones, setVisibleZones] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(ZONES_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  const [mapDarkMode, setMapDarkMode] = useState<boolean>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'true';
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  // Save visible zones to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(ZONES_STORAGE_KEY, JSON.stringify(Array.from(visibleZones)));
      // Mark that zones have been initialized by the user
      localStorage.setItem(ZONES_INITIALIZED_KEY, 'true');
    } catch (error) {
      console.error('Error saving visible zones to localStorage:', error);
    }
  }, [visibleZones]);

  // Save map theme to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mapDarkMode.toString());
    } catch (error) {
      console.error('Error saving map theme to localStorage:', error);
    }
  }, [mapDarkMode]);

  const clearFilters = () => {
    setFilters({});
    localStorage.removeItem(STORAGE_KEY);
  };

  const clearAllSettings = () => {
    setFilters({});
    setVisibleZones(new Set());
    setMapDarkMode(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ZONES_STORAGE_KEY);
    localStorage.removeItem(THEME_STORAGE_KEY);
    localStorage.removeItem(ZONES_INITIALIZED_KEY);
  };

  // Helper function to check if zones have been initialized - memoized to prevent infinite re-renders
  const areZonesInitialized = useCallback(() => {
    return localStorage.getItem(ZONES_INITIALIZED_KEY) === 'true';
  }, []);

  return {
    filters,
    setFilters,
    visibleZones,
    setVisibleZones,
    mapDarkMode,
    setMapDarkMode,
    clearFilters,
    clearAllSettings,
    areZonesInitialized,
  };
};

